// Multi-select store picker. Originally built for the "Invitar miembro"
// form (ADR-0047) and promoted to a shared component for #0424 so the
// checklist-template-per-store admin screen can reuse it with a
// pending-diff overlay (originalIds vs selectedIds).
//
// Presentational: the parent owns the selected-id list and handles the
// role gate / loading / error / zero-stores states. This component owns
// only the populated list + its local search box.
//
// Semantics: a native <fieldset>/<legend> + <input type="checkbox"> group
// (first rule of ARIA — native HTML covers multi-select without a custom
// listbox). `disabled` on the fieldset cascades to every inner control.
// `hasSupervisor` is surfaced as a text badge (color + label, never
// color-only) so already-covered stores read on any assistive tech.

import { useEffect, useId, useState } from "react";

// Minimal structural shape this component depends on — deliberately not
// importing AdminOrgAssetSummary from @bitacorapro/api-types so any
// caller (admin invite form, checklist-template assignment drawer, …)
// can pass its own row shape as long as the fields line up.
export type StorePickerItem = {
  id: string;
  unit: string | null;
  address: string | null;
  assetGroupName?: string | null;
  // True when the store already has a bound supervisor/owner (mirrors
  // AdminOrgAssetSummary.hasSupervisor). Surfaced as a fixed text badge —
  // color + label, never color-only — so callers don't need to supply
  // their own copy for it.
  hasSupervisor?: boolean;
};

// "multi" (default) = the original checkbox group. "single" = a native
// radio group for exactly-one-store roles (store_manager, ADR-0116) —
// still reports through the same `(next: string[]) => void` signature,
// just constrained to a 0- or 1-length array.
export type StorePickerMode = "single" | "multi";

type StorePickerProps = {
  stores: StorePickerItem[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  // Persisted baseline selection (#0424). When provided, each row is
  // classified kept/added/removed/neutral against `selectedIds` and gets
  // a matching `.store-picker__option--*` class + delta tag. Omitted by
  // the original invite-form call site, which keeps today's behavior.
  originalIds?: string[];
  addedLabel?: string;
  removedLabel?: string;
  // Defaults to "multi" so every existing call site (AdminOrgDetail.tsx,
  // ChecklistTemplateAssignDrawer.tsx) is unaffected by omitting it.
  mode?: StorePickerMode;
  // Overrides the whole computed legend string — needed by callers that
  // want role-specific copy (Equipo.tsx's store_manager legend) without
  // hardcoding that copy into this shared component. `| undefined`
  // explicit (exactOptionalPropertyTypes) — callers pass a ternary that
  // resolves to undefined rather than omitting the prop.
  legend?: string | undefined;
  // Extra class(es) appended to the root <fieldset> — e.g. Equipo.tsx's
  // `store-picker--all-selected` state hook (ADR-0116). Appended, never
  // replaces, the base `store-picker`/`store-picker--single` classes.
  className?: string | undefined;
};

// Below this many stores a search box is noise; above it the flat list
// needs filtering to stay usable (an org can have 200+ tiendas).
const SEARCH_THRESHOLD = 8;

const DEFAULT_ADDED_LABEL = "Nueva";
const DEFAULT_REMOVED_LABEL = "Se quitará";

export function StorePicker({
  stores,
  selectedIds,
  onChange,
  disabled,
  originalIds,
  addedLabel = DEFAULT_ADDED_LABEL,
  removedLabel = DEFAULT_REMOVED_LABEL,
  mode = "multi",
  legend,
  className,
}: StorePickerProps) {
  const [search, setSearch] = useState("");
  // (a) — immediate, undebounced single-mode pick announcement.
  const [pickAnnouncement, setPickAnnouncement] = useState("");
  // (b) — debounced search-result-count announcement, both modes.
  const [searchAnnouncement, setSearchAnnouncement] = useState("");
  // Unique per mounted instance — this is a shared component, a
  // hardcoded literal `name` would collide if two pickers render at once
  // (e.g. a future side-by-side comparison view).
  const radioGroupName = useId();

  const q = search.trim().toLowerCase();
  const visible =
    q.length === 0
      ? stores
      : stores.filter((s) =>
          [s.unit, s.address, s.assetGroupName]
            .filter((v): v is string => Boolean(v))
            .some((v) => v.toLowerCase().includes(q))
        );

  const selected = new Set(selectedIds);
  const original = originalIds ? new Set(originalIds) : null;
  const allVisibleSelected = visible.length > 0 && visible.every((s) => selected.has(s.id));

  // (b) fires ~500ms after the last search-box keystroke (or a
  // selection change, so multi-mode's trailing count stays fresh without
  // a second announcement channel). Single mode drops the "·
  // seleccionada(s)" suffix — (a) already covers selection there.
  useEffect(() => {
    const timer = setTimeout(() => {
      const visibleCount = `${visible.length} tienda${visible.length === 1 ? "" : "s"} visible${
        visible.length === 1 ? "" : "s"
      }.`;
      setSearchAnnouncement(
        mode === "single"
          ? visibleCount
          : `${visibleCount.slice(0, -1)} · ${selectedIds.length} seleccionada${
              selectedIds.length === 1 ? "" : "s"
            }.`
      );
    }, 500);
    return () => clearTimeout(timer);
  }, [search, visible.length, selectedIds.length, mode]);

  const computedLegend =
    mode === "single"
      ? `Tienda a asignar — ${selectedIds.length === 1 ? "1 seleccionada" : "elige 1 tienda"}`
      : `Tiendas a asignar — ${selectedIds.length} de ${stores.length} seleccionada${
          selectedIds.length === 1 ? "" : "s"
        }`;
  const legendText = legend ?? computedLegend;

  function toggle(id: string): void {
    if (disabled) return;
    if (mode === "single") {
      const store = stores.find((s) => s.id === id);
      const label = store ? (store.unit ?? store.address ?? store.id) : id;
      setPickAnnouncement(`Tienda seleccionada: ${label}.`);
      onChange(selected.has(id) ? [] : [id]);
      return;
    }
    onChange(selected.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  }

  function toggleAllVisible(): void {
    if (disabled) return;
    if (allVisibleSelected) {
      const visibleIds = new Set(visible.map((s) => s.id));
      onChange(selectedIds.filter((x) => !visibleIds.has(x)));
    } else {
      const merged = new Set(selectedIds);
      visible.forEach((s) => merged.add(s.id));
      onChange([...merged]);
    }
  }

  const rootClassName = [
    "store-picker",
    mode === "single" ? "store-picker--single" : null,
    className ?? null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <fieldset className={rootClassName} disabled={disabled}>
      <legend className="store-picker__legend">{legendText}</legend>
      {mode === "single" && (
        <span role="status" aria-live="polite" className="cell-meta" style={{ display: "block" }}>
          {pickAnnouncement}
        </span>
      )}
      <span role="status" aria-live="polite" className="cell-meta" style={{ display: "block" }}>
        {searchAnnouncement}
      </span>
      <div className="store-picker__head">
        {stores.length >= SEARCH_THRESHOLD && (
          <input
            type="search"
            className="input store-picker__search"
            placeholder="Buscar tienda…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar tienda"
          />
        )}
        {mode === "multi" && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={toggleAllVisible}
            disabled={disabled || visible.length === 0}
          >
            {allVisibleSelected ? "Quitar selección" : "Seleccionar todas"}
          </button>
        )}
      </div>
      <div className="store-picker__list">
        {visible.length === 0 ? (
          <span className="store-picker__empty cell-meta">
            No hay tiendas que coincidan con «{search}».
          </span>
        ) : (
          visible.map((store) => {
            const label = store.unit ?? store.address ?? store.id;
            const isSelected = selected.has(store.id);
            // Pending-diff state (#0424) — only meaningful when the
            // caller passed originalIds. Neutral (today's behavior)
            // otherwise.
            const wasOriginal = original?.has(store.id) ?? false;
            const diffState = !original
              ? null
              : wasOriginal && isSelected
                ? "kept"
                : !wasOriginal && isSelected
                  ? "added"
                  : wasOriginal && !isSelected
                    ? "removed"
                    : null;
            const optionClass = diffState
              ? `store-picker__option store-picker__option--${diffState}`
              : "store-picker__option";
            return (
              <label key={store.id} className={optionClass}>
                {mode === "single" ? (
                  <input
                    type="radio"
                    name={radioGroupName}
                    checked={isSelected}
                    onChange={() => toggle(store.id)}
                  />
                ) : (
                  <input type="checkbox" checked={isSelected} onChange={() => toggle(store.id)} />
                )}
                <span className="store-picker__option-label">
                  {label}
                  {store.assetGroupName && (
                    <span className="store-picker__option-sub"> · {store.assetGroupName}</span>
                  )}
                  {store.hasSupervisor && (
                    <span
                      className={
                        isSelected
                          ? "store-picker__badge store-picker__badge--conflict"
                          : "store-picker__badge"
                      }
                      title="Esta tienda ya tiene un supervisor asignado"
                    >
                      ya asignada
                    </span>
                  )}
                </span>
                {(diffState === "added" || diffState === "removed") && (
                  <span className={`store-picker__delta-tag store-picker__delta-tag--${diffState}`}>
                    {diffState === "added" ? addedLabel : removedLabel}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </fieldset>
  );
}
